<div class="focus-page">
  <header class="focus-header">
    <h1 class="text-title">Focus</h1>
    <p>Choose the essential tasks to action today, then allocate time for them in <a routerLink="/today">Today</a>.</p>
  </header>

  <section *ngIf="initialLoading()" class="loading">Loading focus view…</section>
  <section *ngIf="error()" class="lm-notice lm-notice--danger">
    {{ error() }}
    <button type="button" class="lm-btn" (click)="load()">Retry</button>
  </section>

  <ng-container *ngIf="!initialLoading() && !error() && view() as currentView">
    <section class="focus-card">
      <h3>Today's Priorities</h3>
      <p *ngIf="priorityDailyTarget() !== null" class="focus-target">
        Target: {{ priorityDailyTarget() }} tasks
      </p>
      <p *ngIf="overPriorityTarget()" class="lm-notice lm-notice--warning">
        You are above today's priority target. Keep only the must-do tasks here.
      </p>

      <ul class="task-list">
        <li *ngFor="let task of todaysPrioritiesActive()" class="lm-row lm-row--priority">
          <button
            type="button"
            class="lm-icon-btn lm-icon-btn--ghost lm-icon-btn--priority"
            aria-pressed="true"
            [disabled]="isSubmitting(task.id)"
            (click)="unprioritizeTask(task)"
            [attr.aria-label]="'Priority: ' + task.title"
            title="Remove from priorities">
            <svg lucidePin class="lm-icon lm-icon--fill" aria-hidden="true"></svg>
          </button>
          <div class="lm-row__main">
            <span class="lm-row__title">{{ task.title }}</span>
          </div>
          <div class="row-actions">
            <button
              type="button"
              class="lm-btn"
              [disabled]="isSubmitting(task.id)"
              (click)="completeTask(task)">
              Complete
            </button>
          </div>
        </li>
        <li *ngIf="todaysPrioritiesActive().length === 0" class="empty">No priorities selected for today yet.</li>
      </ul>

      <h4 *ngIf="todaysPrioritiesCompleted().length > 0">Completed priorities</h4>
      <ul class="task-list completed" *ngIf="todaysPrioritiesCompleted().length > 0">
        <li *ngFor="let task of todaysPrioritiesCompleted()" class="lm-row lm-row--done">
          <button
            type="button"
            class="lm-icon-btn lm-icon-btn--ghost lm-icon-btn--priority"
            aria-pressed="true"
            [disabled]="isSubmitting(task.id)"
            (click)="unprioritizeTask(task)"
            [attr.aria-label]="'Priority: ' + task.title"
            title="Remove from priorities">
            <svg lucidePin class="lm-icon lm-icon--fill" aria-hidden="true"></svg>
          </button>
          <div class="lm-row__main">
            <span class="lm-row__title">{{ task.title }}</span>
          </div>
        </li>
      </ul>

      <h3>Today's Focus</h3>
      <p *ngIf="focusDailyTarget() !== null" class="focus-target">
        Target: {{ focusDailyTarget() }} tasks
      </p>
      <p *ngIf="overFocusTarget()" class="lm-notice lm-notice--warning">
        You are above today's focus target. Keep only what must get done.
      </p>

      <ul class="task-list">
        <li *ngFor="let task of todaysFocusActive()" class="lm-row">
          <button
            type="button"
            class="lm-icon-btn lm-icon-btn--ghost lm-icon-btn--focus"
            aria-pressed="true"
            [disabled]="isSubmitting(task.id)"
            (click)="unfocusTask(task)"
            [attr.aria-label]="'Focus: ' + task.title"
            title="Remove from focus">
            <svg lucideStar class="lm-icon lm-icon--fill" aria-hidden="true"></svg>
          </button>
          <div class="lm-row__main">
            <span class="lm-row__title">{{ task.title }}</span>
          </div>
          <div class="row-actions">
            <button
              type="button"
              class="lm-icon-btn lm-icon-btn--ghost lm-icon-btn--priority"
              [attr.aria-pressed]="isTaskPrioritizedToday(task)"
              [disabled]="isSubmitting(task.id)"
              (click)="isTaskPrioritizedToday(task) ? unprioritizeTask(task) : prioritizeTask(task)"
              [attr.aria-label]="'Priority: ' + task.title"
              [attr.title]="isTaskPrioritizedToday(task) ? 'Remove from priorities' : 'Add to priorities'">
              <svg lucidePin class="lm-icon" [class.lm-icon--fill]="isTaskPrioritizedToday(task)" aria-hidden="true"></svg>
            </button>
            <button
              type="button"
              class="lm-btn"
              [disabled]="isSubmitting(task.id)"
              (click)="completeTask(task)">
              Complete
            </button>
          </div>
        </li>
        <li *ngIf="todaysFocusActive().length === 0" class="empty">No tasks in focus for today yet.</li>
      </ul>

      <h4 *ngIf="todaysFocusCompleted().length > 0">Completed today</h4>
      <ul class="task-list completed" *ngIf="todaysFocusCompleted().length > 0">
        <li *ngFor="let task of todaysFocusCompleted()" class="lm-row lm-row--done">
          <div class="lm-row__main">
            <span class="lm-row__title">{{ task.title }}</span>
          </div>
          <div class="row-actions">
            <button
              type="button"
              class="lm-icon-btn lm-icon-btn--ghost lm-icon-btn--focus"
              aria-pressed="true"
              [disabled]="isSubmitting(task.id)"
              (click)="unfocusTask(task)"
              [attr.aria-label]="'Focus: ' + task.title"
              title="Remove from focus">
              <svg lucideStar class="lm-icon lm-icon--fill" aria-hidden="true"></svg>
            </button>
          </div>
        </li>
      </ul>
    </section>

    <section class="focus-groups" #focusGroups>
      <h3>Tasks by activity</h3>
      <div *ngFor="let group of currentView.groups" class="focus-group">
        <div class="focus-group-header">
          <h4>{{ group.name }}</h4>
          <button
            type="button"
            class="lm-icon-btn lm-icon-btn--ghost add-task-btn"
            [attr.aria-expanded]="activeAddGroupId() === group.activityTypeId"
            [disabled]="addingTask()"
            (click)="activeAddGroupId() === group.activityTypeId ? cancelAddTask() : openAddTask(group)"
            [attr.aria-label]="activeAddGroupId() === group.activityTypeId ? 'Cancel adding task for ' + group.name : 'Add task for ' + group.name"
            [attr.title]="activeAddGroupId() === group.activityTypeId ? 'Cancel adding task' : 'Add task'">
            <svg *ngIf="activeAddGroupId() === group.activityTypeId; else addTaskIcon" lucideX class="lm-icon" aria-hidden="true"></svg>
            <ng-template #addTaskIcon><svg lucidePlus class="lm-icon" aria-hidden="true"></svg></ng-template>
          </button>
        </div>
        <div *ngIf="activeAddGroupId() === group.activityTypeId" class="group-add-task-row">
          <input
            type="text"
            maxlength="500"
            placeholder="Add task"
            [value]="newTaskTitle"
            (input)="newTaskTitle = $any($event.target).value"
            (keydown.enter)="addTaskToGroup(group); $event.preventDefault()"
          />
          <button
            type="button"
            class="lm-btn lm-btn--primary"
            [disabled]="addingTask() || !newTaskTitle.trim()"
            (click)="addTaskToGroup(group)">
            {{ addingTask() ? 'Adding…' : 'Add' }}
          </button>
          <button
            type="button"
            class="lm-btn"
            [disabled]="addingTask()"
            (click)="cancelAddTask()">
            Cancel
          </button>
        </div>
        <ul class="task-list">
          <li
            *ngFor="let task of group.tasks"
            class="lm-row"
            [class.lm-row--priority]="isTaskPrioritizedToday(task)">
            <button
              type="button"
              class="lm-icon-btn lm-icon-btn--ghost lm-icon-btn--focus"
              [attr.aria-pressed]="isTaskFocusedToday(task)"
              [disabled]="isSubmitting(task.id)"
              (click)="isTaskFocusedToday(task) ? unfocusTask(task) : focusTask(task)"
              [attr.aria-label]="'Focus: ' + task.title"
              [attr.title]="isTaskFocusedToday(task) ? 'Remove from focus' : 'Add to focus'">
              <svg lucideStar class="lm-icon" [class.lm-icon--fill]="isTaskFocusedToday(task)" aria-hidden="true"></svg>
            </button>
            <div class="lm-row__main">
              <span class="lm-row__title">{{ task.title }}</span>
            </div>
            <div class="row-actions">
              <button
                *ngIf="isTaskFocusedToday(task)"
                type="button"
                class="lm-icon-btn lm-icon-btn--ghost lm-icon-btn--priority"
                [attr.aria-pressed]="isTaskPrioritizedToday(task)"
                [disabled]="isSubmitting(task.id)"
                (click)="isTaskPrioritizedToday(task) ? unprioritizeTask(task) : prioritizeTask(task)"
                [attr.aria-label]="'Priority: ' + task.title"
                [attr.title]="isTaskPrioritizedToday(task) ? 'Remove from priorities' : 'Add to priorities'">
                <svg lucidePin class="lm-icon" [class.lm-icon--fill]="isTaskPrioritizedToday(task)" aria-hidden="true"></svg>
              </button>
            </div>
          </li>
          <li *ngIf="group.tasks.length === 0" class="empty">No active tasks.</li>
        </ul>
      </div>
    </section>
  </ng-container>
</div>
